import type { Layout } from '../../../data/types';
import type { AABB, PolygonWithHoles, Vec2 } from '../../../geo/types';
import { stripPolygon } from '../../../geo/polyline';
import { pointInPolygon, ringBounds } from '../../../geo/polygon';

/** Whether a layout point lies in water. */
export type WaterTest = (p: Vec2) => boolean;

export const NO_WATER: WaterTest = () => false;

interface BoundedPolygon {
  readonly polygon: PolygonWithHoles;
  readonly bounds: AABB;
}

function bounded(polygon: PolygonWithHoles): BoundedPolygon {
  return { polygon, bounds: ringBounds(polygon.outer) };
}

function inBounds(p: Vec2, box: AABB): boolean {
  return p[0] >= box.minX && p[0] <= box.maxX && p[1] >= box.minY && p[1] <= box.maxY;
}

/*
 * The layout's water as the fortifications need it: every channel's strip, every water area and the
 * IJ, less the dry ground its dams record where the gates stand in their moats. The site model
 * resolves junctions and mouths more finely; which side of a wall the water lies on does not depend
 * on that, and the fortifications receive the layout alone.
 */
export function layoutWaterTest(water: Layout['water']): WaterTest {
  const polygons = [
    bounded(water.ij),
    ...water.areas.map((area) => bounded(area.polygon)),
    ...water.channels.map((channel) =>
      bounded({ outer: stripPolygon(channel.centerline, channel.widths), holes: [] })
    ),
  ];
  const dams = water.dams.map((dam) => bounded(dam.polygon));
  const inAny = (p: Vec2, list: readonly BoundedPolygon[]) =>
    list.some(({ polygon, bounds }) => inBounds(p, bounds) && pointInPolygon(p, polygon));
  return (p) => inAny(p, polygons) && !inAny(p, dams);
}
